import { useEffect, useRef, useState } from 'react'
import { Palette as PaletteIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../api/api'
import { useAuth } from '../contexts/AuthContext'
import { applyPalette, currentPalette, holdLocalPalette, markLocalPaletteChange, onPaletteChange, Palette, PALETTES } from '../utils/palette'

/**
 * Выбор одной из шести палитр ECMIT. Применяется сразу и запоминается в браузере; при едином входе
 * ещё и сохраняется у человека в «Входе», чтобы палитра была одной во всех службах.
 */
export default function PaletteMenu() {
  const { gateEnabled } = useAuth()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Palette | null>(currentPalette)
  const ref = useRef<HTMLDivElement | null>(null)

  // Палитра может смениться извне (обновление с сервера): отметка в меню должна за ней успевать
  useEffect(() => onPaletteChange(() => setSelected(currentPalette())), [])

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (palette: Palette) => {
    setSelected(palette)
    markLocalPaletteChange()
    applyPalette(palette)
    if (gateEnabled) {
      api.saveGatePalette(palette)
        .then((result) => { if (!result.synced) { holdLocalPalette(); toast('Палитра сохранена только в этом браузере') } })
        .catch(() => { holdLocalPalette(); toast('Палитра сохранена только в этом браузере') })
    }
  }

  const label = PALETTES.find((palette) => palette.id === selected)?.label

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        title="Цветовая палитра"
        aria-label="Цветовая палитра"
        aria-expanded={open}
      >
        <PaletteIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="group"
          aria-label="Цветовая палитра"
          className="fixed left-3 right-3 z-50 mt-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:left-auto sm:right-0 sm:w-72"
        >
          <div className="flex flex-wrap items-center gap-2">
            {PALETTES.map((palette) => (
              <button
                key={palette.id}
                type="button"
                title={palette.label}
                aria-label={palette.label}
                aria-pressed={selected === palette.id}
                onClick={() => pick(palette.id)}
                style={{ background: palette.color }}
                className={`h-7 w-7 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(100,116,139,0.6)] dark:border-gray-800 ${selected === palette.id ? 'ring-2 ring-gray-900 ring-offset-1 dark:ring-white' : ''}`}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{label || 'Классический синий (палитра не выбрана)'}</p>
        </div>
      )}
    </div>
  )
}
